//#region Imports

import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { ButtonRadiusEnum } from '../../shared/components/button/types/button-radius.enum';
import { ButtonSizeEnum } from '../../shared/components/button/types/button-size.enum';
import { ButtonTypeEnum } from '../../shared/components/button/types/button-type.enum';
import { CourseFilterComponent } from '../../shared/components/course-filter/course-filter.component';
import { CourseTableComponent } from '../../shared/components/course-table/course-table.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { InputComponent } from '../../shared/components/input/input.component';
import { ModalAction } from '../../shared/modals/modal-actions/modal-action.interface';
import { CourseStatusTextRecord } from '../../shared/models/enums/course-status.enum';
import { CourseFilterParams, CourseStatusFilter } from '../../shared/models/interfaces/course-filter-value.interface';
import { FilterPill, FilterPillTypeEnum } from '../../shared/models/interfaces/filter-pill.interface';
import { Course } from '../../shared/models/interfaces/course.interface';
import { Category } from '../../shared/models/interfaces/category.interface';
import { PaginationDto } from '../../shared/services/pagination/models/interfaces/pagination-dto';
import { Sorts } from '../../shared/services/pagination/models/typies/sorts.type';
import { ModalService } from '../../shared/services/modal/modal.service';
import { CourseService } from '../../shared/services/course/course.service';
import { CourseListingMode, CourseListingModeDescriptionRecord, CourseListingModeTextRecord } from '../../shared/models/enums/course-listing-mode.enum';
import { createCancelConfirmButtons, openConfirmModal } from '../../helpers/modal.helper';
import { ToastService } from '../../shared/components/toast/service/toast.service';
import { CategoryService } from '../../shared/services/category/category.service';
import { NgClass } from '@angular/common';
import { COURSE_DELETE_RETENTION_HOURS } from '../../shared/utils/variables';
import { getCrudErrorMessage } from '../../shared/utils/functions';

//#endregion

@Component({
  selector: 'l-course',
  standalone: true,
  imports: [
    IconComponent,
    ButtonComponent,
    InputComponent,
    ReactiveFormsModule,
    CourseFilterComponent,
    CourseTableComponent,
    NgClass,
],
  templateUrl: './course.component.html',
})
export class CourseComponent implements OnInit, OnDestroy {

  //#region Constructor

  constructor(
    private readonly courseService: CourseService,
    private readonly modalService: ModalService,
    private readonly toastService: ToastService,
    private readonly categoryService: CategoryService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
  ) {}

  //#endregion

  //#region Public Properties

  public readonly buttonTypeEnum = ButtonTypeEnum;

  public readonly buttonSizeEnum = ButtonSizeEnum;

  public readonly buttonRadiusEnum = ButtonRadiusEnum;

  public readonly searchControl = new FormControl('');

  public loadErrorMessage = '';

  public isLoading: boolean = false;

  public limit: number = 10;

  public page: number = 1;

  public sort: Sorts = { field: 'updatedAt', order: 'DESC' };

  public listingMode: CourseListingMode = CourseListingMode.PUBLISHED;

  public readonly courseListingMode = CourseListingMode;

  public selectedStatusFilter?: CourseStatusFilter = 'all';

  public selectedCategoryFilters?: Category[] = [];

  public selectedDateRangeFilter?: [Date | null, Date | null];

  public source: PaginationDto<Course> = {
    data: [],
    page: 1,
    pageCount: 0,
    count: 0,
    total: 0,
  };

  public categories: Category[] = [];

  public courses: Course[] = [];

  //#endregion

  //#region Private Properties

  private readonly destroy$$ = new Subject<void>();

  private readonly modalWidth = 'fit-content';

  //#endregion

  //#region Getters

  public get pageTitle(): string {
    return CourseListingModeTextRecord[this.listingMode];
  }

  public get pageDescription(): string {
    return CourseListingModeDescriptionRecord[this.listingMode];
  }

  public get showAdvancedFilters(): boolean {
    return this.listingMode !== CourseListingMode.TRASH;
  }

  public get appliedFilterPills(): FilterPill[] {
    const pills: FilterPill[] = [];

    if (this.selectedStatusFilter && this.selectedStatusFilter !== 'all') {
      pills.push({
        key: 'status',
        type: FilterPillTypeEnum.STATUS,
        label: `Status: ${CourseStatusTextRecord[this.selectedStatusFilter]}`,
      });
    }

    if (this.selectedCategoryFilters && this.selectedCategoryFilters.length > 0) {
      this.selectedCategoryFilters.forEach((category) => {
        pills.push({
          key: `category-${category.id}`,
          type: FilterPillTypeEnum.CATEGORY,
          label: `Categoria: ${category.name}`,
          categoryId: category.id,
        });
      });
    }

    if (this.selectedDateRangeFilter?.[0] || this.selectedDateRangeFilter?.[1]) {
      const [startDate, endDate] = this.selectedDateRangeFilter;
      const formatDate = (date: Date | null): string => {
        if (!date) {
          return '...';
        }

        return new Intl.DateTimeFormat('pt-BR').format(date);
      };

      pills.push({
        key: 'date',
        type: FilterPillTypeEnum.DATE,
        label: `Data: ${formatDate(startDate)} - ${formatDate(endDate)}`,
      });
    }

    return pills;
  }

  //#endregion

  //#region Lifecycle Hooks

  public async ngOnInit(): Promise<void> {
    this.route.data.pipe(
      takeUntil(this.destroy$$),
    ).subscribe((data) => {
      this.listingMode = (data['listingMode'] as CourseListingMode | undefined) ?? CourseListingMode.PUBLISHED;
      this.resetFiltersForListingMode();
      void this.loadCourses(1);
    });

    this.searchControl.valueChanges.pipe(
      debounceTime(350),
      takeUntil(this.destroy$$),
    ).subscribe((value) => {
      void this.loadCourses(1, value || '');
    });

    await this.loadCategories();
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  //#endregion

  //#region Public Methods

  public navigateToCreateCourse(): void {
    this.router.navigate(['/courses/create']);
  }

  public async onChangePage(page: number): Promise<void> {
    await this.loadCourses(page);
  }

  public async onChangeSort(sort?: Sorts): Promise<void> {
    this.sort = sort || { field: 'updatedAt', order: 'DESC' };
    await this.loadCourses(1);
  }

  public onFilterApplied(filter: CourseFilterParams): void {
    this.selectedStatusFilter = filter.status;
    this.selectedCategoryFilters = filter.categories ? [...filter.categories] : [];
    this.selectedDateRangeFilter = filter.createdAtRange;

    void this.loadCourses(this.page);
  }

  public removeFilterPill(pill: FilterPill): void {
    if (pill.type === FilterPillTypeEnum.STATUS) {
      this.selectedStatusFilter = 'all';
    }

    if (pill.type === FilterPillTypeEnum.CATEGORY && pill.categoryId) {
      this.selectedCategoryFilters = this.selectedCategoryFilters?.filter((category) => category.id !== pill.categoryId) || [];
    }

    if (pill.type === FilterPillTypeEnum.DATE) {
      this.selectedDateRangeFilter = undefined;
    }

    void this.loadCourses(1);
  }

  public getRowActions(course: Course): ModalAction[] {
    if (this.listingMode === CourseListingMode.TRASH) {
      return [
        {
          label: 'Restaurar',
          icon: 'restore',
          callback: () => this.openCourseRestoreModal(course),
        },
        {
          label: 'Excluir',
          icon: 'delete',
          colorClass: 'text-lDanger',
          callback: () => this.openCoursePermanentDeleteModal(course),
        },
      ];
    }

    const actions: ModalAction[] = [
      {
        label: 'Editar',
        icon: 'edit',
        routerLink: ['/courses', course.id, 'edit'],
      },
    ];

    if (this.listingMode === CourseListingMode.PUBLISHED) {
      actions.push({
        label: 'Desativar',
        icon: 'power_settings_new',
        callback: () => this.openCourseDeactivateModal(course),
      });
    }

    actions.push({
      label: 'Excluir',
      icon: 'delete',
      colorClass: 'text-lDanger',
      callback: () => this.openCourseDeleteModal(course),
    });

    return actions;
  }

  //#endregion

  //#region Private Methods

  private async loadCourses(page: number, search?: string): Promise<void> {
    try {
      this.isLoading = true;
      this.loadErrorMessage = '';

      const searchValue = (search ?? this.searchControl.value ?? '').trim();

      const searchParams: CourseFilterParams = {
        status: this.selectedStatusFilter,
        categories: this.selectedCategoryFilters,
        createdAtRange: this.selectedDateRangeFilter,
        keyword: searchValue,
        sort: this.sort,
        limit: this.limit,
        page,
        listingMode: this.listingMode,
      };

      this.source = await this.courseService.search({ ...searchParams });
      this.courses = this.source.data;

      this.page = page;
    } catch (error) {
      this.source = {
        data: [],
        page,
        pageCount: 0,
        count: 0,
        total: 0,
      };
      this.courses = [];
      this.loadErrorMessage = getCrudErrorMessage(error) || 'Não foi possível carregar os cursos.';
      this.toastService.error(this.loadErrorMessage, 'Oops...');
    } finally {
      this.isLoading = false;
    }
  }

  private async loadCategories(): Promise<void> {
    try {
      this.categories = (await this.categoryService.getMany()).data;
    } catch (error) {
      this.toastService.error(getCrudErrorMessage(error), 'Oops...');
    }
  }

  private resetFiltersForListingMode(): void {
    this.selectedStatusFilter = 'all';
    this.selectedCategoryFilters = [];
    this.selectedDateRangeFilter = undefined;
  }

  private async deactivateCourse(courseId: string): Promise<void> {
    try {
      this.isLoading = true;
      await this.courseService.deactivate(courseId);
      await this.loadCourses(this.page);
      this.toastService.success('Curso desativado com sucesso.');
    } catch (error) {
      this.toastService.error(getCrudErrorMessage(error), 'Oops...');
    } finally {
      this.isLoading = false;
    }
  }

  private async restoreCourse(courseId: string): Promise<void> {
    try {
      this.isLoading = true;
      await this.courseService.restore(courseId);
      await this.loadCourses(this.page);
      this.toastService.success('Curso restaurado com sucesso.');
    } catch (error) {
      this.toastService.error(getCrudErrorMessage(error), 'Oops...');
    } finally {
      this.isLoading = false;
    }
  }

  private async delete(courseId: string): Promise<void> {
    try {
      this.isLoading = true;
      await this.courseService.permanentDelete(courseId);
      await this.loadCourses(this.page);
      this.toastService.success('Curso excluído permanentemente.');
    } catch (error) {
      this.toastService.error(getCrudErrorMessage(error), 'Oops...');
    } finally {
      this.isLoading = false;
    }
  }

  private async softDelete(courseId: string): Promise<void> {
    try {
      this.isLoading = true;
      await this.courseService.softDelete(courseId);
      await this.loadCourses(this.page);
      this.toastService.success('Curso excluído com sucesso.');
    } catch (error) {
      this.toastService.error(getCrudErrorMessage(error), 'Oops...');
    } finally {
      this.isLoading = false;
    }
  }

  private openCourseDeactivateModal(course: Course): void {
    openConfirmModal(
      this.modalService,
      this.modalWidth,
      {
        title: 'Desativar curso',
        description: 'Tem certeza que deseja desativar este curso?',
        buttons: createCancelConfirmButtons('Desativar'),
      },
      async () => {
        await this.deactivateCourse(course.id);
      },
      this.destroy$$,
    );
  }

  private openCourseDeleteModal(course: Course): void {
    openConfirmModal(
      this.modalService,
      this.modalWidth,
      {
        title: 'Excluir curso',
        description: `Tem certeza que deseja excluir este curso? Ele será movido para a lixeira e excluído permanentemente após ${COURSE_DELETE_RETENTION_HOURS} horas.`,
        buttons: createCancelConfirmButtons('Excluir', ButtonTypeEnum.DANGER),
      },
      async () => {
        await this.softDelete(course.id);
      },
      this.destroy$$,
    );
  }

  private openCourseRestoreModal(course: Course): void {
    openConfirmModal(
      this.modalService,
      this.modalWidth,
      {
        title: 'Restaurar curso',
        description: 'Tem certeza que deseja restaurar este curso?',
        buttons: createCancelConfirmButtons('Restaurar'),
      },
      async () => {
        await this.restoreCourse(course.id);
      },
      this.destroy$$,
    );
  }

  private openCoursePermanentDeleteModal(course: Course): void {
    openConfirmModal(
      this.modalService,
      this.modalWidth,
      {
        title: 'Excluir permanentemente',
        description: 'Essa ação irá excluir permanentemente o curso selecionado. Tem certeza que deseja continuar?',
        buttons: createCancelConfirmButtons('Sim', ButtonTypeEnum.DANGER),
      },
      async () => {
        await this.delete(course.id);
      },
      this.destroy$$,
    );
  }

  //#endregion

}
